import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';

import Typography from '../../typography/typography';
import DatePicker from '../../date-picker/date-picker';
import DateTimePicker from '../../date-time-picker/date-time-picker';
import TextField from '../../text-field/text-field';
import UploadMedia from '../../upload-media/upload-media';

import * as yup from 'yup';
import _ from 'lodash';
import dayjs from 'dayjs';
import PhoneInput from '../../phone-input/phone-input';
import Checkbox from '../../checkbox/checkbox';
import SearchableSelect from '../../searchable-select';
import TimePicker from '../../time-picker/time-picker';

interface IInlineEditFields {
	cell: any;
	column: any;
	row: any;
	table: any;
	col: any;
	callback: (value: any, column: any, rowId: any) => any;
	errors: any;
	validationSchema: any;
	setValidationErrors: any;
	onChangeRowField?: (e: any, type: string, row: any) => any;
	getSelectedData: any
}
const InlineEditFields: React.FC<IInlineEditFields> = ({
	// cell,
	column,
	row,
	// table,
	col,
	// callback,
	errors,
	validationSchema,
	setValidationErrors,
	onChangeRowField,
	getSelectedData,
	syncFieldValue
}) => {

	const [inputValue, setInputValue] = useState<any>();
	const [isInitialSet, setIsInitialSet] = useState<any>(false);
	const customFilter = useRef(null)
	// ** Getting only editing enabled fields.
	const editCols = row
		.getAllCells()
		.map((col: any) => col.column.columnDef)
		.filter(
			(col: any) =>
				col?.enableEditing === undefined || col?.enableEditing === true
		);

	const editVariant = col.editVariant;
	const fieldProperties = column.columnDef?.editProperties || {};

	// ** Default and Dynamic disbaling a field with row data
	let fieldDisbled = false;
	if (fieldProperties?.disable) {
		if (typeof fieldProperties.disable === 'function') {
			fieldDisbled = Boolean(fieldProperties?.disable(row, row._valuesCache));
		} else {
			fieldDisbled = Boolean(fieldProperties.disable);
		}
	}

	const handleCustomFilter = useCallback(async (row, values) => {

		try {
			if (typeof fieldProperties.customeFilter === 'function') {
				const filter = await fieldProperties.customeFilter(row, values);
				customFilter.current = filter;
				return filter;
			} else {
				customFilter.current = fieldProperties?.customeFilter;
			}
		} catch (error) {
			return null;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fieldProperties, row, row._valuesCache]);


	useEffect(() => {
		handleCustomFilter(row, row._valuesCache)
	}, [handleCustomFilter, row, row._valuesCache]);
	// ** Setting a default value for edit mode.
	const valueKey = fieldProperties?.valueKey;
	let defaultValue: any = '';

	if (editVariant === 'select' && fieldProperties?.is_multiple) {
		defaultValue = valueKey ? _.get(row.original, valueKey, []) : [];
	} else {
		defaultValue = valueKey
			? _.get(row.original, valueKey, row.original[column.id])
			: row.original[column.id];

		// For select fields, if we have a string or numeric value but need an object, try to find the matching option
		if (editVariant === 'select' && (typeof defaultValue === 'string' || typeof defaultValue === 'number') && col?.editSelectOptions) {
			const matchingOption = col.editSelectOptions.find((option: any) =>
				option.value === defaultValue || option.id === defaultValue || option.name === defaultValue || String(option.id) === String(defaultValue)
			);
			if (matchingOption) {
				defaultValue = matchingOption;
			}
		}
	}

	// ** Validate a field with Material Table Validation Schema.
	const validate = async (value: any) => {
		try {
			// const v = await validationSchema.validateAt( // * * This line has commented for rerendering purpose
			await validationSchema.validateAt(
				column.id,
				{
					// ...row._valuesCache,
					[column.id]: value
				},
				{
					abortEarly: false
				}
			);
			// if (v) { // * * The if condition has commented for rerendering purpose
			const fe: any = {}; // field error
			for (const e in errors) {
				if (e != column.id) {
					fe[e] = errors[e];
				}
			}
			setValidationErrors(fe);
			// }
		} catch (err) {
			if (err instanceof yup.ValidationError) {
				const e: Record<string, string> = {};
				err.inner.forEach((error) => {
					if (error.path) {
						e[error.path] = error.message;
					}
				});
				const em = { ...errors, ...e };
				setValidationErrors(em);
			}
		}
	};



	// ** Detect onChnage to perform the action on an input change.
	const onChange = async (e: any, type: string, selectedData: any) => {
		const data = selectedData
		let value;
		switch (type) {
			case 'date':
			case 'file':
			case 'image':
			case 'phone':
			case 'time':
				value = e;
				// setInputValue({ ...inputValue, [column.id]: e });
				break;
			case 'text':
			case 'number':
				value = e.target.value;
				// setInputValue({ ...inputValue, [column.id]: value });
				break;
			case 'checkbox':
				value = e.target.checked;
				// setInputValue({ ...inputValue, [column.id]: value });
				break;
			default:
				value = e.target.value;
				break;
		}
		setInputValue({ ...inputValue, [column.id]: value });

		if (onChangeRowField) {
			const v = await onChangeRowField(e, type, row, col, data);
			if (v) {
				row._valuesCache = { ...row._valuesCache, ...v };
				setInputValue({ ...inputValue, ...v });
			}
		}
		setInputValue({ ...inputValue, [column.id]: value });
		row._valuesCache = { ...row._valuesCache, [column.id]: value };
		syncFieldValue(row.id, column.id, value);

		await validate(value);
	};

	useEffect(() => {
		setInputValue({ ...inputValue, ...row._valuesCache });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [row._valuesCache]);

	useEffect(() => {
		if (!isInitialSet) {
			const editFields = Object.keys(row._valuesCache || {});
			const dValues: any = {};

			row.getAllCells().forEach((cell) => {
				if (
					cell.column.columnDef?.enableEditing === undefined ||
					cell.column.columnDef?.enableEditing === true
				) {
					const { is_multiple, valueKey } =
						cell.column.columnDef?.editProperties || {};
					const cellEditVariant = cell.column.columnDef?.editVariant;

					if (editFields.includes(cell.column.id)) {
						const vl = _.get(row.original, valueKey);
						if (cellEditVariant === 'select' && is_multiple) {
							dValues[cell.column.id] = vl
								? Array.isArray(vl)
									? vl
									: [vl]
								: [];
						} else if (cellEditVariant === 'date' || cellEditVariant === 'date_time' || cellEditVariant === 'time') {
							dValues[cell.column.id] = vl
								? dayjs(vl).isValid()
									? dayjs(vl)
									: undefined
								: dayjs(row.original[cell.column.id]).isValid()
									? dayjs(row.original[cell.column.id])
									: undefined;
						} else {
							dValues[cell.column.id] = _.get(
								row.original,
								valueKey,
								row.original[cell.column.id]
							);
						}
					}
				}
			});
			// setFieldsValues(dValues);
			row._valuesCache = dValues;
			setInputValue(dValues);
			setIsInitialSet(true);
		}
	}, [row, editVariant, isInitialSet]);
	return (
		<>
			{(!editVariant || editVariant === 'number') && (
				<TextField
					defaultValue={defaultValue}
					// onBlur={(e) => onBlur(e)}
					// onKeyDown={onKeyDown}
					onChange={(e) => onChange(e, editVariant)}
					value={inputValue?.[column.id]}
					autoFocus={editCols?.[0]?.accessorKey === column.id}
					type={editVariant || 'text'}
					error={errors?.[column.id] ? true : false}
					helperText={errors?.[column.id]}
					placeholder={fieldProperties?.placeholder || ''}
					name={fieldProperties?.field_name || ''}
					disabled={fieldDisbled}
				/>
			)}
			{editVariant === 'select' && (
				<Box display='flex' flexDirection='column' width='100%'>
					<SearchableSelect
						name={fieldProperties?.field_name || ''}
						defaultValue={defaultValue}
						placeholder={fieldProperties?.placeholder || ''}
						searchPlaceholder={`Search ${col.header}`}
						onChange={(e, row) => onChange(e, 'select', row)}
						apiType={fieldProperties?.apiType}
						autoFocus={editCols?.[0]?.accessorKey === column.id}
						options={col?.editSelectOptions}
						customeFilter={customFilter.current}
						filterKey={fieldProperties?.filterKey}
						labelKey={fieldProperties?.labelKey}
						valueKey={fieldProperties?.bindingKey}
						attributes={fieldProperties.attributes}
						isInternal={fieldProperties?.isInternal || !fieldProperties?.apiType}
						multiple={fieldProperties?.is_multiple}
						value={
							fieldProperties?.is_multiple
								? Array.isArray(inputValue?.[column.id])
									? inputValue?.[column.id]
									: []
								: inputValue?.[column.id]
						}
						getSelectedData={(row) => getSelectedData(fieldProperties?.field_name, row)}
						disabled={fieldDisbled}
					/>

					{errors?.[column.id] && (
						<Typography color='theme.error.600' type='s5'>
							{errors?.[column.id]}
						</Typography>
					)}
				</Box>
			)}
			{editVariant === 'date' && (
				<DatePicker
					name={fieldProperties?.field_name || ''}
					defaultValue={defaultValue}
					onChange={(v) => onChange(v, 'date')}
					textFieldProps={{
						autoFocus: editCols?.[0]?.accessorKey === column.id
					}}
					error={errors?.[column.id] ? true : false}
					helperText={errors?.[column.id]}
					format={fieldProperties?.dateFormat}
					placeholder={
						fieldProperties?.placeholder || fieldProperties?.dateFormat
					}
					value={
						row._valuesCache[column.id] && dayjs(row._valuesCache[column.id]).isValid()
							? dayjs(row._valuesCache[column.id])
							: null
					}
					disabled={fieldDisbled}
					minDate={fieldProperties?.min_date}
					maxDate={fieldProperties?.max_date}
				/>
			)}
			{editVariant === 'date_time' && (
				<DateTimePicker
					name={fieldProperties?.field_name || ''}
					defaultValue={defaultValue}
					onChange={(v) => onChange(v, 'date')}
					textFieldProps={{
						autoFocus: editCols?.[0]?.accessorKey === column.id
					}}
					error={errors?.[column.id] ? true : false}
					helperText={errors?.[column.id]}
					format={fieldProperties?.dateFormat}
					placeholder={
						fieldProperties?.placeholder || fieldProperties?.dateFormat
					}
					value={
						dayjs(row._valuesCache[column.id]).isValid()
							? dayjs(row._valuesCache[column.id])
							: undefined
					}
					disabled={fieldDisbled}
				/>
			)}
			{editVariant === 'time' && (
				<TimePicker
					name={fieldProperties?.field_name || ''}
					label=''
					value={
						dayjs(row._valuesCache[column.id]).isValid()
							? dayjs(row._valuesCache[column.id])
							: undefined
					}
					onChange={(v) => onChange(v, 'time')}
					error={errors?.[column.id] ? true : false}
					helperText={errors?.[column.id]}
					placeholder={fieldProperties?.placeholder || 'HH:mm A'}
					disabled={fieldDisbled}
					is24hrFormat={fieldProperties?.is24hrFormat}
				/>
			)}
			{(editVariant === 'file' || editVariant === 'image') && (
				<Box display='flex' flexDirection='column'>
					<UploadMedia
						name={fieldProperties?.field_name || 'table_file'}
						dataPosition={0}
						dataName='table_file'
						label=''
						type={editVariant}
						max_size={10}
						onChange={(v) => onChange(v, 'file')}
						error={errors?.[column.id] ? true : false}
						multiple={Boolean(column?.is_multiple)}
						value={row._valuesCache[column.id]}
					/>
					{errors?.[column.id] && (
						<Typography color='theme.error.600' type='s5'>
							{errors?.[column.id]}
						</Typography>
					)}
				</Box>
			)}
			{editVariant === 'phone' && (
				<PhoneInput
					name={fieldProperties?.field_name || ''}
					default_value={defaultValue}
					onChange={(v) => onChange(v, 'phone')}
					textFieldProps={{
						autoFocus: editCols?.[0]?.accessorKey === column.id
					}}
					error={errors?.[column.id] ? true : false}
					helperText={errors?.[column.id]}
					format={fieldProperties?.dateFormat}
					placeholder={
						fieldProperties?.placeholder || fieldProperties?.dateFormat
					}
					value={row._valuesCache?.[column.id]}
					disabled={fieldDisbled}
					isInlineEditing={true}
				/>
			)}
			{editVariant === 'checkbox' && (
				<Checkbox
					name={fieldProperties?.field_name || ''}
					defaultChecked={Boolean(defaultValue)}
					// checked={Boolean(defaultValue)}
					onChange={(v) => onChange(v, 'checkbox')}
					error={errors?.[column.id] ? true : false}
					helperText={errors?.[column.id]}
					format={fieldProperties?.dateFormat}
					placeholder={
						fieldProperties?.placeholder || fieldProperties?.dateFormat
					}
					value={row._valuesCache?.[column.id]}
					disabled={fieldDisbled}
				/>
			)}
		</>
	);
};

export default InlineEditFields;
